import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SUCCESS_STORIES } from "./successStoriesData";

/**
 * SuccessStoryCard — detail page at /success-stories/:id
 *
 * Resolves the story via URL param, then renders the full layout in this order:
 *   1. Header        — title + intro + client logo/tagline
 *   2. Hero image
 *   3. Mission       — name, subtitle, paragraphs, external CTA
 *   4. Challenge     — 3 constraint cards on a tinted band
 *   5. Wide image
 *   6. Design steps  — 3 numbered columns (optional "credit" tag)
 *   7. Architecture  — numbered items w/ labeled detail image
 *   8. Impact table  — Metric / Results / Value Delivered
 *   9. Legacy        — narrative + milestones table
 *   10. Timeline     — Milestone / Date table
 *   11. End image
 *   12. Quotes
 *   13. Final CTA    — routes to /contact
 */

const GRADIENT = "linear-gradient(90deg, #00858F 0%, #E6007E 100%)";
/* News-style banner accent — magenta → muted violet (matches NEWS header) */
const NEWS_GRADIENT = "linear-gradient(90deg, #E6007E 0%, #6E5BA8 100%)";
const TEAL = "#00858F";
const PINK = "#E6007E";
const WHITE = "#ffffff";


const normalizeHex = (hex: string) =>
    hex.startsWith("#") ? hex : `#${hex}`;

const hexToRgba = (hex: string, alpha: number) => {
    const cleanHex = normalizeHex(hex).replace("#", "");

    const r = parseInt(cleanHex.slice(0, 2), 16);
    const g = parseInt(cleanHex.slice(2, 4), 16);
    const b = parseInt(cleanHex.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const GradientText = ({ children }: { children: React.ReactNode }) => (
    <span
        className="text-md uppercase tracking-widest font-bold"
        style={{
            background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
        }}
    >
    {children}
  </span>
);

/* Band background used for the "challenge" section — subtle teal fade */
const TEAL_BAND =
    "linear-gradient(180deg, rgba(0,133,143,0.25) 0%, rgba(0,133,143,0.05) 100%)";

const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderHighlightedText = (
    text: string,
    terms: string[]
): React.ReactNode => {
    const cleanTerms = terms.filter(Boolean);

    if (!cleanTerms.length) return text;

    const regex = new RegExp(`(${cleanTerms.map(escapeRegExp).join("|")})`, "gi");

    return text.split(regex).map((part, index) => {
        const isMatch = cleanTerms.some(
            (term) => part.toLowerCase() === term.toLowerCase()
        );

        return isMatch ? (
            <strong key={index} className="font-extrabold text-white">
                {part}
            </strong>
        ) : (
            <React.Fragment key={index}>{part}</React.Fragment>
        );
    });
};

const SuccessStoryCard: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const story = SUCCESS_STORIES.find((s) => s.story_id === id);

    /* Scroll to top whenever we land on a different story */
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [id]);

    /* Unknown slug → bounce back to the listing */
    if (!story) {
        return <Navigate to="/success-stories" replace />;
    }

    const themeColor = normalizeHex(story.themeColor);
    const themeColorSoft = hexToRgba(themeColor, 0.55);
    const themeColorMuted = hexToRgba(themeColor, 0.7);

    const themeGradient = `linear-gradient(to bottom, #031017 0%, ${hexToRgba(
        themeColor,
        0.45
    )} 55%, ${hexToRgba(themeColor, 0.8)} 100%)`;

    const highlightTerms = [
        "Revolv Space",
        story.client.name,
        ...(story.client.highlightAliases ?? []),
    ];
    return (
        <article className="bg-black text-white w-full overflow-hidden pt-24">
            <Helmet>
                <title>{`${story.client.name} — ${story.mission.name} | Revolv Space`}</title>
                <meta name="description" content={story.intro} />
            </Helmet>

            {/* ───────── 1. HEADER ───────── */}
            <section className="px-6 md:px-16 lg:px-24 pt-10 pb-10">
                <div className="grid md:grid-cols-[1fr_auto] gap-10 items-start">
                    <div className="max-w-4xl">
                        <div className="flex justify-start pb-4">
                            <Link
                                to="/success-stories"
                                className="text-xs uppercase tracking-[0.25em] text-gray-400 hover:text-white transition-colors"
                            >
                                ← All success stories
                            </Link>
                        </div>
                        <div className="mb-5">
                            <GradientText>
                                SUCCESS STORY: {`${story.client.name}`}

                            </GradientText>
                        </div>
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase leading-[1.15]">
                            {story.title}
                        </h1>
                        {story.subtitle && (
                            <p className="mt-3 text-xl md:text-2xl font-bold uppercase tracking-wide text-gray-200">
                                {story.subtitle}
                            </p>
                        )}
                        <p className="mt-8 text-gray-300 leading-relaxed uppercase">
                            {renderHighlightedText(story.intro, highlightTerms)}
                        </p>
                    </div>

                    <div className="flex flex-col-reverse items-start h-full md:items-end gap-4 md:pt-4 ">


                        <p className="text-2xl md:text-right text-gray-200 uppercase tracking-wide max-w-[24rem] mt-10 mb-2">
                            {story.client.tagline}
                        </p>
                        <img
                            src={story.client.logo}
                            alt={`${story.client.name} logo`}
                            className="h-60 md:h-52 object-contain"
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                        />
                    </div>
                </div>
            </section>

            {/* ───────── 1.5 NEWS BANNER ───────── */}
            {story.descriptionHighlight && (
                <section className="px-6 md:px-16 lg:px-24 pt-2 pb-12 md:pb-16">
                    {story.subtitleHighlight && (
                        <p className="text-sm md:text-base font-bold uppercase tracking-[0.2em] text-white mb-4 md:mb-5">
                            {story.subtitleHighlight}
                        </p>
                    )}
                    <h2 className="text-3xl md:text-5xl font-extrabold uppercase leading-[1.1]">
                        {story.descriptionHighlight.split("\n").map((line, i, arr) => {
                            const isAccent = i === arr.length - 1;
                            return (
                                <span
                                    key={i}
                                    className="block"
                                    style={
                                        isAccent
                                            ? {
                                                background: NEWS_GRADIENT,
                                                WebkitBackgroundClip: "text",
                                                WebkitTextFillColor: "transparent",
                                                backgroundClip: "text",
                                            }
                                            : { color: WHITE }
                                    }
                                >
                                    {line}
                                </span>
                            );
                        })}
                    </h2>
                </section>
            )}

            {/* ───────── 2. HERO IMAGE ───────── */}
            <figure className="relative">
                <img
                    src={story.heroImage}
                    alt={story.mission.name}
                    className="w-full object-cover"
                />
                {story.heroImageCaption && (
                    <figcaption className="mt-3 mr-24 text-right text-[10px] md:text-xs text-white uppercase tracking-widest">
                        {story.heroImageCaption}
                    </figcaption>
                )}
            </figure>


            {/* ───────── 3. MISSION ───────── */}
            <section className="px-6 md:px-16 lg:px-24 py-16">
                <h2 className="text-3xl md:text-4xl font-bold uppercase">
                    {story.mission.name}
                </h2>
                <p className="mt-3 text-lg md:text-xl text-gray-200 max-w-4xl uppercase">
                    {renderHighlightedText(story.mission.subtitle, highlightTerms)}
                </p>
                <div className="mt-8 space-y-5 text-gray-300 leading-relaxed max-w-4xl uppercase">
                    {story.mission.paragraphs.map((p, i) => (
                        <p key={i}>{renderHighlightedText(p, highlightTerms)}</p>
                    ))}
                </div>
                <a
                    href={story.mission.ctaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-10 rounded-full h-12 text-white px-10 font-bold uppercase text-sm transform transition-transform duration-200 hover:scale-110"
                    style={{ background: GRADIENT }}
                >
                    {story.mission.ctaText}
                </a>
            </section>

            {/* ───────── 4. CHALLENGE ───────── */}
            {/* ───────── 4. CHALLENGE ───────── */}
            <section className="mx-6 px-6 md:px-6 lg:px-20 py-16 border-t border-white/15">
                <h2 className="text-3xl md:text-4xl font-bold uppercase leading-tight max-w-4xl">
                    {story.challenge.heading}
                </h2>

                <p className="mt-5 text-gray-200 max-w-4xl leading-relaxed uppercase">
                    {renderHighlightedText(story.challenge.description, highlightTerms)}
                </p>

                <div className="mt-12 grid md:grid-cols-3 gap-6">
                    {story.challenge.items.map((item, i) => (
                        <div key={i} className="p-6">
                            <div className="mb-3 h-[5px] w-full"
                                 style={{ backgroundColor: themeColor }}/>
                            <h3 className="text-base md:text-lg font-bold uppercase text-white tracking-wider">
                                {item.title}
                            </h3>

                            <p className="mt-3 text-gray-300 text-sm leading-relaxed uppercase">
                                {item.description}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ───────── 5. WIDE IMAGE ───────── */}
            <figure className="relative">
                <img src={story.wideImage} alt="" className="w-full object-cover" />
                {story.wideImageCaption && (
                    <figcaption className="mt-3 mr-24 text-right text-[10px] md:text-xs text-white uppercase tracking-widest">
                        {story.wideImageCaption}
                    </figcaption>
                )}
            </figure>

            {/* ───────── 6. DESIGN STEPS ───────── */}
            <section className="px-6 md:px-16 lg:px-24 py-16">
                <h2 className="text-2xl md:text-3xl font-bold uppercase leading-tight max-w-4xl">
                    {story.design.heading}
                </h2>
                <h2 className="text-3xl md:text-4xl font-bold uppercase leading-tight max-w-4xl"
                    style={{
                        background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                    }}>
                    {story.design.subheading}
                </h2>
                <div className="mt-12 grid md:grid-cols-3 gap-5">
                    {story.design.steps.map((step) => (

                        <div
                            key={step.number}
                            className=" overflow-hidden flex flex-col"
                        >
                          <span className="py-4 text-6xl font-bold text-white leading-none">
                                    {step.number}
                                </span>
                            <div
                                className=" py-4 flex items-start gap-4"
                            >
                                <h3 className="font-bold uppercase leading-tight text-2xl md:text-2xl">
                                    {step.title}
                                </h3>
                            </div>
                            <div className="p-5 flex-1 flex flex-col gap-4">
                                {step.credit && (
                                    <p className="text-[10px] uppercase italic tracking-widest text-white">
                                        {step.credit}
                                    </p>
                                )}
                                <ul className="space-y-3 text-sm text-white uppercase leading-relaxed">
                                    {step.bullets.map((b, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span
                                                className="shrink-0 mt-[0.45rem] h-[5px] w-[5px] rounded-0"
                                                style={{ background: WHITE }}
                                            />
                                            <span>{renderHighlightedText(b, highlightTerms)}</span>                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ───────── 7. ARCHITECTURE ───────── */}
            {/* ───────── 7. ARCHITECTURE ───────── */}
            <section className="px-6 md:px-12 lg:px-20 xl:px-24 py-16 md:py-20">
                <h2 className="text-2xl md:text-3xl font-bold uppercase leading-tight max-w-4xl">
                    {story.architecture.heading}
                </h2>
                <h2 className="text-3xl md:text-4xl font-bold uppercase leading-tight max-w-4xl"
                    style={{
                        background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                    }}>
                    {story.architecture.subheading}</h2>

                <p className="mt-6 md:mt-8 max-w-4xl text-white text-base md:text-lg xl:text-xl font-semibold uppercase leading-[1.15]">
                    {renderHighlightedText(story.architecture.description, highlightTerms)}
                </p>

                <div className="mt-14 md:mt-16 space-y-14 md:space-y-16 xl:space-y-20">
                    {story.architecture.items.map((item) => (
                        <div
                            key={item.number}
                            className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_clamp(320px,38vw,560px)] gap-8 md:gap-10 xl:gap-16 items-start"
                        >
                            <div className="max-w-[620px]">
          <span className="block text-6xl md:text-7xl xl:text-8xl font-extrabold text-white leading-none">
            {item.number}
          </span>

                                <h3 className="mt-6 md:mt-8 text-2xl md:text-3xl xl:text-4xl font-extrabold uppercase leading-[0.95] text-white">
                                    {item.title}
                                </h3>

                                <p className="mt-4 md:mt-6 max-w-[560px] text-white text-sm md:text-base xl:text-lg  uppercase leading-[1.15]">
                                    {renderHighlightedText(item.description, highlightTerms)}
                                </p>

                                {item.productSlug && (
                                    <Link to={`/products/${item.productSlug}/`}>
                                        <button
                                            className="mt-8 rounded-full h-12 text-white px-10 font-bold uppercase text-sm transform transition-transform duration-200 hover:scale-110"
                                            style={{ background: GRADIENT }}
                                        >
                                            Discover {item.productSlug}
                                        </button>
                                    </Link>
                                )}
                            </div>

                            <figure className="w-full max-w-[560px] lg:justify-self-end">
                                <div
                                    className="relative w-full aspect-[16/10] md:aspect-[16/9] overflow-hidden"
                                    style={
                                        item.isProduct === false
                                            ? undefined
                                            : { background: themeGradient }
                                    }
                                >
                                    <img
                                        src={item.image}
                                        alt={item.imageLabel}
                                        className={
                                            item.isProduct === false
                                                ? "absolute inset-0 h-full w-full object-cover"
                                                : "absolute inset-0 mr-2 pr-10 mb-4 h-full w-full object-contain object-right"
                                        }
                                        onError={(e) => {
                                            (e.currentTarget as HTMLImageElement).style.display = "none";
                                        }}
                                    />

                                    {/* Legibility scrim for full-bleed photos only */}
                                    {item.isProduct === false && (
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                                    )}

                                    <figcaption className="absolute left-4 bottom-4 md:left-6 md:bottom-6 xl:left-8 xl:bottom-8 text-white text-xl md:text-2xl xl:text-4xl font-extrabold uppercase tracking-[0.04em] drop-shadow-[0_3px_10px_rgba(0,0,0,0.35)]">
                                        {item.imageLabel}
                                    </figcaption>
                                </div>

                                {item.credit && (
                                    <p className="mt-3 text-right text-[10px] md:text-xs text-white uppercase tracking-widest">
                                        {item.credit}
                                    </p>
                                )}
                            </figure>
                        </div>
                    ))}
                </div>
            </section>

            {/* ───────── 8. IMPACT TABLE ───────── */}
            <section className="mx-6 border-t border-white/15 px-6 md:px-12 lg:px-20 xl:px-24 py-16 md:py-20 bg-black text-white">
                <h2 className="text-2xl md:text-3xl font-bold uppercase leading-tight max-w-4xl">
                    {story.impact.heading}
                </h2>

                <div className="mt-12 hidden md:grid md:grid-cols-3 gap-8 md:gap-10 items-start">
                    {/* COLUMN 1 */}
                    <div className="grid grid-rows-[88px_auto] md:grid-rows-[96px_auto]">
                        <div className="flex items-end">
                            <h3 className="text-2xl md:text-3xl font-extrabold uppercase leading-[0.9] mb-3">
                                Metric
                            </h3>
                        </div>

                        <div className="min-h-[360px] border-[3px] px-6 py-8 md:px-8 md:py-10 flex flex-col"
                             style={{ borderColor: themeColor }}
                        >
                            {story.impact.rows.map((row, i) => (
                                <div
                                    key={i}
                                    className={`flex flex-1 items-center ${i !== story.impact.rows.length - 1 ? "border-b border-white" : ""}`}
                                >
                                    <p className="text-lg md:text-[1.05rem] uppercase leading-[1.1] text-white">
                                        {row.metric}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* COLUMN 2 */}
                    <div className="grid grid-rows-[88px_auto] md:grid-rows-[96px_auto]">
                        <div className="flex items-end">
                            <h3 className="text-2xl md:text-3xl font-extrabold uppercase leading-[0.9] mb-3">
                                Results
                            </h3>
                        </div>

                        <div className="min-h-[360px] border-[8px] px-6 py-8 md:px-8 md:py-10 flex flex-col bg-black"
                             style={{ borderColor: themeColorSoft }}
                        >
                            {story.impact.rows.map((row, i) => (
                                <div
                                    key={i}
                                    className={`flex flex-1 items-center ${i !== story.impact.rows.length - 1 ? "border-b border-white" : ""}`}
                                >
                                    <p className="text-lg md:text-[1.05rem] font-extrabold uppercase leading-[1.1] text-white">
                                        {row.results}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* COLUMN 3 */}
                    <div className="grid grid-rows-[88px_auto] md:grid-rows-[96px_auto]">
                        <div className="flex items-end">
                            <h3 className="text-2xl md:text-3xl font-extrabold uppercase leading-[0.9] mb-3">
                                Value
                                <br />
                                delivered
                            </h3>
                        </div>

                        <div className="min-h-[360px] px-6 py-8 md:px-8 md:py-10 flex flex-col"
                             style={{ backgroundColor: themeColorMuted }}
                        >
                            {story.impact.rows.map((row, i) => (
                                <div
                                    key={i}
                                    className={`flex flex-1 items-center ${i !== story.impact.rows.length - 1 ? "border-b border-white" : ""}`}
                                >
                                    <p className="text-lg md:text-[1.05rem] font-extrabold uppercase leading-[1.1] text-white">
                                        {row.value}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Mobile stacked layout */}
                <div className="mt-10 md:hidden space-y-6">
                    {story.impact.rows.map((row, i) => (
                        <div
                            key={i}
                            className="border-[3px] px-6 py-6"
                            style={{ borderColor: themeColor }}
                        >
                            <p className="text-sm uppercase tracking-wide text-white leading-snug mb-3 italic">
                                {row.metric}
                            </p>
                            <p
                                className="text-base font-extrabold uppercase leading-tight mb-3"
                                style={{
                                    background: GRADIENT,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                {renderHighlightedText(row.results, highlightTerms)}
                            </p>
                            <p className="text-2xl font-extrabold uppercase leading-snug text-white">
                                {row.value}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ───────── 9. LEGACY / SCALE ───────── */}
            <section className="px-6 md:px-12 lg:px-20 xl:px-24 py-16 md:py-20 bg-black text-white">
                <h2 className="text-2xl md:text-3xl font-bold uppercase leading-tight max-w-4xl">
                    {story.legacy.heading}
                </h2>

                <p className="mt-6 max-w-5xl text-base md:text-lg lg:text-xl uppercase leading-[1.15] text-white">
                    {renderHighlightedText(story.legacy.description, highlightTerms)}
                </p>

                <div
                    className={`mt-14 grid grid-cols-1 gap-10 xl:gap-16 items-start ${
                        story.legacy.sideText
                            ? "xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.6fr)]"
                            : "xl:grid-cols-1"
                    }`}
                >
                    {story.legacy.sideText && (
                        <div className="max-w-[420px] xl:pt-[72px]">
                            <p className="text-xl md:text-2xl font-extrabold uppercase leading-[1.08] text-white">
                                {renderHighlightedText(story.legacy.sideText, highlightTerms)}
                            </p>
                        </div>
                    )}

                    <div className="hidden md:grid md:grid-cols-2 gap-8 md:gap-10 items-start">
                        <div className="grid grid-rows-[72px_auto] md:grid-rows-[80px_auto]">
                            <div className="flex items-end">
                                <h3 className="text-2xl md:text-3xl mb-3 font-extrabold uppercase leading-[0.9] text-white">
                                    Milestone
                                </h3>
                            </div>

                            <div className="min-h-[360px] md:min-h-[440px] border-[8px] px-8 py-8 md:px-10 md:py-10 flex flex-col bg-black"
                                 style={{ borderColor: themeColorMuted }}
                            >
                                {story.legacy.milestones.map((m, i) => (
                                    <div
                                        key={i}
                                        className={`flex flex-1 items-center ${
                                            i !== story.legacy.milestones.length - 1 ? "border-b border-white/90" : ""
                                        }`}
                                    >
                                        <p className="text-xl md:text-2xl font-extrabold uppercase leading-[1.08] text-white">
                                            {m.milestone}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-rows-[72px_auto] md:grid-rows-[80px_auto]">
                            <div className="flex items-end">
                                <h3 className="text-2xl mb-3 md:text-3xl font-extrabold uppercase leading-[0.9] text-white">
                                    Number
                                </h3>
                            </div>

                            <div className="min-h-[360px] md:min-h-[440px]  px-8 py-8 md:px-10 md:py-10 flex flex-col"
                                 style={{ backgroundColor: themeColorMuted }}
                            >
                                {story.legacy.milestones.map((m, i) => (
                                    <div
                                        key={i}
                                        className={`flex flex-1 items-center ${
                                            i !== story.legacy.milestones.length - 1 ? "border-b border-white/90" : ""
                                        }`}
                                    >
                                        <p className="text-4xl md:text-5xl font-extrabold uppercase leading-none text-white">
                                            {m.number}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Mobile stacked layout */}
                    <div className="mt-10 md:hidden space-y-6">
                        {story.legacy.milestones.map((m, i) => (
                            <div
                                key={i}
                                className="border-[3px] px-6 py-6"
                                style={{ borderColor: themeColorMuted }}
                            >
                                <p className="text-lg font-extrabold uppercase leading-snug text-white mb-2">
                                    {m.milestone}
                                </p>
                                <p className="text-5xl font-extrabold uppercase leading-none text-white">
                                    {m.number}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───────── 10. TIMELINE ───────── */}
            <section className="px-6 md:px-12 lg:px-20 xl:px-24 py-16 md:py-20 bg-black text-white">
                <h2 className="text-2xl md:text-3xl font-bold uppercase leading-tight max-w-4xl">
                    {story.timeline.heading}
                </h2>

                <div className="mt-14 hidden md:grid md:grid-cols-2 gap-8 md:gap-10 items-start">
                    <div className="grid grid-rows-[72px_auto] md:grid-rows-[80px_auto]">
                        <div className="flex items-end">
                            <h3 className="text-2xl md:text-3xl mb-3 font-extrabold uppercase leading-[0.9] text-white">
                                Milestone
                            </h3>
                        </div>

                        <div className="min-h-[360px] md:min-h-[560px] border-[8px] px-8 py-8 md:px-10 md:py-10 flex flex-col bg-black"
                             style={{ borderColor: themeColor }}
                        >
                            {story.timeline.rows.map((r, i) => (
                                <div
                                    key={i}
                                    className={`flex flex-1 items-center ${
                                        i !== story.timeline.rows.length - 1 ? "border-b border-white/90" : ""
                                    }`}
                                >
                                    <p className="text-xl md:text-2xl font-extrabold uppercase leading-[1.08] text-white">
                                        {r.milestone}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-rows-[72px_auto] md:grid-rows-[80px_auto]">
                        <div className="flex items-end">
                            <h3 className="text-2xl md:text-3xl mb-3 font-extrabold uppercase leading-[0.9] text-white">
                                Date
                            </h3>
                        </div>

                        <div className="min-h-[360px] md:min-h-[560px] px-8 py-8 md:px-10 md:py-10 flex flex-col"
                             style={{ backgroundColor: themeColorMuted }}
                        >
                            {story.timeline.rows.map((r, i) => (
                                <div
                                    key={i}
                                    className={`flex flex-1 items-center ${
                                        i !== story.timeline.rows.length - 1 ? "border-b border-white/90" : ""
                                    }`}
                                >
                                    <p className="text-2xl md:text-3xl lg:text-4xl font-extrabold uppercase leading-none text-white">
                                        {r.date}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Mobile stacked layout */}
                <div className="mt-10 md:hidden space-y-6">
                    {story.timeline.rows.map((r, i) => (
                        <div
                            key={i}
                            className="border-[3px] px-6 py-6"
                            style={{ borderColor: themeColor }}
                        >
                            <p className="text-sm uppercase tracking-wide text-white mb-2 italic">
                                {r.milestone}
                            </p>
                            <p className="text-3xl font-extrabold uppercase leading-tight text-white">
                                {r.date}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ───────── 11. END IMAGE ───────── */}
            <figure className="relative">
                <img src={story.endImage} alt="" className="w-full object-cover" />

                {story.endImageCaption && (
                    <figcaption className="mt-3 mr-24 text-right text-[10px] md:text-xs text-white uppercase tracking-widest">
                        {story.endImageCaption}
                    </figcaption>
                )}
            </figure>

            {/* ───────── 12. QUOTES ───────── */}
            <section className="px-6 md:px-16 lg:px-24 py-20">
                <div className="max-w-5xl mx-auto space-y-12">
                    {story.quotes.map((q, i) => (
                        <blockquote
                            key={i}
                            className="border-l-4 pl-6 md:pl-8"
                            style={{ borderColor: themeColor }}                        >
                            <p className=" uppercase text-lg md:text-xl text-gray-200 leading-relaxed">
                                “{renderHighlightedText(q.text, highlightTerms)}”
                            </p>
                            <cite className="block mt-5 text-sm uppercase tracking-[0.2em] text-gray-400 not-italic">
                                — {q.author}
                            </cite>
                        </blockquote>
                    ))}
                </div>
            </section>

            {/* ───────── 13. FINAL CTA ───────── */}
            <section className="px-6 md:px-16 lg:px-24 py-20 text-center">
                <h2 className="text-3xl md:text-4xl font-bold uppercase">
                    {story.cta.heading}
                </h2>
                <p className="mt-5 text-gray-300 max-w-2xl mx-auto leading-relaxed uppercase">
                    {renderHighlightedText(story.cta.description, highlightTerms)}
                </p>
                <Link to={story.cta.buttonLink}>
                    <button
                        className="mt-10 rounded-full h-12 text-white px-10 font-bold uppercase text-sm transform transition-transform duration-200 hover:scale-110"
                        style={{ background: GRADIENT }}
                    >
                        {story.cta.buttonText}
                    </button>
                </Link>
            </section>

            {/* ───────── Back to listing ───────── */}
            <div className="flex justify-center pb-16">
                <Link
                    to="/success-stories"
                    className="text-xs uppercase tracking-[0.25em] text-gray-400 hover:text-white transition-colors"
                >
                    ← All success stories
                </Link>
            </div>
        </article>
    );
};

export default SuccessStoryCard;
